import { useApolloClient } from '@apollo/client';
import {
  Notification,
  NotificationAIScheduleType,
  type NotificationChannelMessage,
  NotificationChannelMessageType,
  NotificationVoiceChannelType,
} from '@copera/notification';
import { parseHtmlToPlainText } from '@copera/tiptap/parser';
import { useWorkspaceInfoRefetch } from '@modules/workspace/hooks/useWorkspaceInfoRefetch';
import { useWorkspaceInfoUsersById } from '@modules/workspace/providers/WorkspaceInfo/hooks/useWorkspaceInfoUsersById';
import { useQueryClient } from '@tanstack/react-query';
import * as bc from 'lib0/broadcastchannel';
import { useSnackbar } from 'notistack';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import type { AiChat } from 'src/@types/ai';
import { AICallChannel } from 'src/@types/chat';
import { ConversationMessageContentType } from 'src/@types/conversation';
import { PATH_DASHBOARD } from 'src/Router/paths';
import { useAuthContext } from 'src/auth/useAuthContext';
import { useBrowserNotification } from 'src/features/Notifications/shared/useBrowserNotification';
import { usePlaySound } from 'src/features/RTCBar/GatheringSpacesSoundBoard/usePlaySound';
import { AudioType, useAudio } from 'src/features/audio';
import { useRecentStore } from 'src/hooks/recents';
import { useDocsQuery } from 'src/pages/dashboard/DocsPage';
import { useChatContext } from 'src/provider/ChatContext';
import { useLivekitRTCState } from 'src/provider/VoiceChannel/livekit/useLivekitRTCState';
import {
  getConversations,
  getConversationsSuccess,
  markConversationAsUnread,
  setConversation,
} from 'src/redux/slices/chat';
import { useDispatch, useSelector } from 'src/redux/store';
import { NotificationStatus } from 'src/requests/notifications.requests';
import { useCurrentConversation } from 'src/sections/@dashboard/ai/Chat/state/useCurrentConversation';
import { useAIScheduleCallInvitedModal } from 'src/sections/@dashboard/ai/Schedule/AIScheduleCallDialog';
import { parseBoardToGql } from 'src/sections/@dashboard/board/BoardPage/BoardSocketListeners';
import {
  addBoardTableToCache,
  addBoardToCache,
} from 'src/sections/@dashboard/board/shared/apolloCache';
import { ChatMessageNotification } from 'src/sections/@dashboard/chat/ChatConversation/shared/ChatMessageNotification';
import { checkIfMutedChannel } from 'src/sections/@dashboard/chat/ChatConversation/shared/utils';
import { useVoiceChannelInvitedModal } from 'src/sections/@dashboard/chat/ChatVoiceChannel/VoiceChannelInvite/VoiceChannelCallingDialog';
import { useSocketEvent } from 'src/socket';
import { onAiChatUpdate } from 'src/socket/listeners/ai';
import {
  onAiScheduleAccepted,
  onAiScheduleDeclined,
  onAiScheduleInvite,
  onAiScheduleNotAnswered,
  onAiScheduleUpdate,
} from 'src/socket/listeners/ai-schedule';
import { onBoardCreated, onTableCreated } from 'src/socket/listeners/board';
import {
  onChatChannelDelete,
  onChatChannelUpdate,
  onChatNewChannel,
  onChatNewMessage,
  onReadMessage,
  onUpdateMessage,
} from 'src/socket/listeners/chat';
import { onDocsRefetch } from 'src/socket/listeners/docs';
import {
  onUserRecentsDelete,
  onUserRecentsUpsert,
} from 'src/socket/listeners/user';
import {
  onPlaySound,
  onVoiceChannelAIAnswer,
  onVoiceChannelAILock,
  onVoiceChannelAIUnlock,
  onVoiceChannelAccepted,
  onVoiceChannelDeclined,
  onVoiceChannelInvited,
  onVoiceChannelListUpdated,
  onVoiceChannelRequestUserDisconnect,
  onVoiceChannelUpdated,
} from 'src/socket/listeners/voice-channel';
import { onWorkspaceMembersRefetch } from 'src/socket/listeners/workspace';
import { QueryKey } from 'src/utils/react-query';
import { onNewSession } from '../socket/listeners/gathering';
import { useVoiceChannelContext } from './VoiceChannel';

export function SocketEventsHandler() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const client = useQueryClient();
  const { cache } = useApolloClient();

  const { refetchWorkspaceInfo } = useWorkspaceInfoRefetch();

  const { voiceChannels, setVoiceChannels, disconnectVoiceChannel } =
    useVoiceChannelContext();

  const { playSound } = usePlaySound();

  const {
    myMeetId,
    activeVoiceChannel,
    setChannelLockedBy,
    setAiStatus,
    setActiveSessionId,
  } = useLivekitRTCState();

  const { refetch: refetchDocs } = useDocsQuery();

  const { openVoiceChannelCallingDialog } = useVoiceChannelInvitedModal();
  const { openAICallDialog } = useAIScheduleCallInvitedModal();

  const { user } = useAuthContext();

  const { currentActiveChat } = useChatContext();
  const { currentConversation, setCurrentConversation } =
    useCurrentConversation();

  const { enqueueSnackbar } = useSnackbar();
  const browser = useBrowserNotification();
  const audio = useAudio();
  const { addRecent, removeRecent, fetch } = useRecentStore();

  const channelsMap = useSelector((state) => state.chat.conversations.byId);

  const { usersById } = useWorkspaceInfoUsersById();

  useSocketEvent(
    onChatNewChannel((channel) => {
      if (!channel.parentId) {
        const newChannels = [...Object.values(channelsMap), channel];
        dispatch(getConversationsSuccess(newChannels));
        setVoiceChannels([...voiceChannels, { channel, members: [] }]);
        return;
      }

      const parentChannel = channelsMap[channel.parentId];
      if (parentChannel.subchannels.find((p) => p._id === channel._id)) return;
      const parentChannelUpdated = {
        ...parentChannel,
        subchannels: [...parentChannel.subchannels, channel],
      };

      const nextChannelMap = Object.assign({}, channelsMap, {
        [parentChannelUpdated._id]: parentChannelUpdated,
      });

      dispatch(getConversationsSuccess(Object.values(nextChannelMap)));
    }),
  );

  useSocketEvent(
    onChatChannelUpdate((channel) => {
      dispatch(setConversation(channel));
    }),
  );

  useSocketEvent(
    onReadMessage((socket) => {
      if (!socket?.parentId) {
        const conversation = {
          ...channelsMap[socket.channelId],
          hasUnreadMessages: false,
          lastReadMessage: socket.channelId,
          unreadMessages: channelsMap[socket.channelId]?.unreadMessages?.filter(
            (u) => !socket.messages.includes(u),
          ),
        };
        return dispatch(setConversation(conversation));
      }
      const conversation = channelsMap[socket.parentId];
      const subchannels = conversation.subchannels.map((s) => {
        if (s._id === socket.channelId) {
          return {
            ...s,
            unreadMessages: s.unreadMessages?.filter(
              (u) => !socket.messages.includes(u),
            ),
          };
        }
        return s;
      });
      dispatch(setConversation({ ...conversation, subchannels }));
    }),
  );

  useSocketEvent(
    onAiChatUpdate((chat) => {
      client.setQueryData<AiChat[]>([QueryKey.AI_CHATS], (current = []) =>
        current.map((i) => (i._id === chat._id ? { ...i, ...chat } : i)),
      );
      if (currentConversation?._id === chat._id) {
        setCurrentConversation({
          ...currentConversation,
          title: chat.title,
        });
      }
    }),
  );

  useSocketEvent(
    onChatChannelDelete((channel) => {
      const channels = [
        ...Object.values(channelsMap).filter(
          (item) => item._id !== channel._id,
        ),
      ];
      dispatch(getConversationsSuccess(channels));
      if (currentActiveChat === channel._id) {
        navigate(PATH_DASHBOARD.chat.root);
      }
      if (channel._id === activeVoiceChannel?.channel?._id) {
        disconnectVoiceChannel();
      }
    }),
  );

  useSocketEvent(
    onChatNewMessage(async (message) => {
      const isWindowFocused = document.hasFocus();
      if (message.channel !== currentActiveChat || !isWindowFocused) {
        if (message.author === user?._id) return;

        const channel = channelsMap[message.channel];

        dispatch(
          setConversation({
            ...channel,
            messages: [...(channel.messages || []), message],
          }),
        );

        dispatch(
          markConversationAsUnread({
            conversationId: message.channel,
            messages: [message._id],
          }),
        );

        const notification: NotificationChannelMessage = {
          _id: message._id,
          type: NotificationChannelMessageType.NEW,
          owner: message.author,
          workspaceId: message.workspace!,
          status: NotificationStatus.UNREAD,
          senderId: message.author,
          sender: message.author,
          workspace: message.workspace,
          receivers: [],
          createdAt: message.createdAt,
          updatedAt: message.updatedAt,
          data: {
            _id: message._id,
            channelName: channel.name,
            contentType: message.contentType,
            content: message.content || '',
            channelId: message.channel,
            channelType: channel.p2p ? 'p2p' : channel.type,
          },
        };

        if (message.contentType === ConversationMessageContentType.TEXT) {
          notification.data.content = parseHtmlToPlainText(message.content!);
        }

        if (!isWindowFocused) {
          browser.notify(notification as Notification);
        } else {
          enqueueSnackbar(
            <ChatMessageNotification
              message={message}
              notification={notification as Notification}
            />,
            {
              variant: 'default',
              autoHideDuration: 8000,
            },
          );
        }

        const { muteSettings } = channelsMap[message.channel];

        if (checkIfMutedChannel(user?._id || '', message, muteSettings)) return;

        audio.play(AudioType.CHANNEL_NEW_MESSAGE);
      }
    }),
  );

  useSocketEvent(
    onVoiceChannelListUpdated((channels) => {
      setVoiceChannels(channels);
      if (!channels.length) return;
      const isCallingChannel = channels.find((channel) =>
        channel.members.some(
          (member) =>
            member.user._id === user?._id && member.status === 'calling',
        ),
      );
      if (isCallingChannel) {
        // const member = isCallingChannel.members.find(m => m.user._id === user?._id);
        openVoiceChannelCallingDialog({
          channelId: isCallingChannel.channel._id,
          sender: '', // TODO - add sender on return
          type: 'voice-channel',
        });
      }
    }),
  );

  useSocketEvent(
    onVoiceChannelUpdated((newVoiceChannel) => {
      if (
        newVoiceChannel.channel._id === activeVoiceChannel?.channel?._id &&
        (activeVoiceChannel?.channel as AICallChannel).aiCall
      )
        return;
      setVoiceChannels((prevVoiceChannels) =>
        prevVoiceChannels.map((stateVoiceChannel) =>
          stateVoiceChannel.channel._id === newVoiceChannel.channel._id
            ? {
                ...stateVoiceChannel,
                ...newVoiceChannel,
                members: newVoiceChannel.members.map((stateMember) => ({
                  ...stateVoiceChannel.members.find(
                    (newMember) => newMember.user._id === stateMember.user._id,
                  ),
                  ...stateMember,
                  ...(!stateMember.sharing && !stateMember.camera
                    ? { theaterMode: false }
                    : {}),
                })),
                lockedBy: newVoiceChannel.lockedBy,
                usersWaitingInLobby: newVoiceChannel.usersWaitingInLobby,
              }
            : stateVoiceChannel,
        ),
      );
    }),
  );

  useSocketEvent(
    onUpdateMessage(async (message) => {
      if (!message.threadLastMessage) return;
      const isWindowFocused = document.hasFocus();
      const parentChannel = message.threadLastMessage?.channel as any;
      if (parentChannel._id !== currentActiveChat || !isWindowFocused) {
        const conversation = channelsMap[parentChannel?.parentId];
        const subchannels = conversation.subchannels.map((s) => {
          if (s._id === parentChannel?._id) {
            return {
              ...s,
              unreadMessages: [
                ...(s.unreadMessages || []),
                message?.threadLastMessage?._id || '',
              ],
            };
          }
          return s;
        });
        dispatch(setConversation({ ...conversation, subchannels }));
      }
    }),
  );

  // AI CALL SCHEDULE
  useSocketEvent(
    onAiScheduleUpdate((data) => {
      // Update Schedule
    }),
  );

  useSocketEvent(
    onAiScheduleInvite((data) => {
      openAICallDialog(data);
      const isWindowFocused = document.hasFocus();

      const { aiCallId, aiCallTitle } = data;
      if (!isWindowFocused) {
        const notification = {
          _id: aiCallId,
          type: NotificationAIScheduleType.INCOMING_CALL,
          status: NotificationStatus.UNREAD,
          workspaceId: '',
          senderId: '',
          receivers: [],
          data: {
            _id: aiCallId,
            name: aiCallTitle,
          },
        };

        browser.notify(notification as any);
      }
    }),
  );

  useSocketEvent(
    onAiScheduleDeclined((data) => {
      bc.publish('ai-call-invite-close', {
        aiCallId: data.aiCallId,
      });
    }),
  );

  useSocketEvent(
    onAiScheduleAccepted((data) => {
      bc.publish('ai-call-invite-close', {
        aiCallId: data.aiCallId,
      });
    }),
  );

  useSocketEvent(
    onAiScheduleNotAnswered((data) => {
      bc.publish('ai-call-invite-close', {
        aiCallId: data.aiCallId,
      });
    }),
  );

  // VOICE INVITE
  useSocketEvent(
    onVoiceChannelInvited((data) => {
      openVoiceChannelCallingDialog(data);

      const isWindowFocused = document.hasFocus();

      const { channelId, sender, from } = data;
      if (!isWindowFocused) {
        const notification = {
          _id: channelId,
          type: NotificationVoiceChannelType.INCOMING_CALL,
          status: NotificationStatus.UNREAD,
          workspaceId: '',
          senderId: sender,
          receivers: [],
          data: {
            _id: channelId,
            name: channelsMap[channelId].name,
            sender: usersById[sender as string].name,
          },
        };

        browser.notify(notification as any);
      }
    }),
  );

  useSocketEvent(
    onVoiceChannelDeclined((data) => {
      bc.publish('voice-channel-invite-close', {
        channelId: data.channelId,
      });
    }),
  );

  useSocketEvent(
    onVoiceChannelAccepted((data) => {
      bc.publish('voice-channel-invite-close', {
        channelId: data.channelId,
      });
    }),
  );

  // VOICE SESSION
  useSocketEvent(
    onNewSession(({ session }: { session: string }) => {
      setActiveSessionId(session);
    }),
  );

  // VOICE SOUNDS
  useSocketEvent(
    onPlaySound((data) => {
      playSound(data);
    }),
  );

  // useSocketEvent(
  //   onVoiceChannelDeclined((data) => {
  //     console.log(data);
  //     updatedMemberInfo(
  //       data.channelId,
  //       data.from as string,
  //       {
  //         inviteStatus: "declined"
  //       }
  //     );
  //   })
  // );
  //
  // useSocketEvent(
  //   onVoiceChannelAccepted((data) => {
  //     console.log(data);
  //   })
  // );
  //
  // useSocketEvent(
  //   onVoiceChannelNotAnswered((data) => {
  //     console.log(data);
  //     updatedMemberInfo(
  //       data.channelId,
  //       data.from as string,
  //       {
  //         inviteStatus: "not-answered"
  //       }
  //     );
  //   })
  // );

  //

  useSocketEvent(
    onVoiceChannelRequestUserDisconnect(() => {
      console.log('SOCKET USER DISCONNECTED');
      // disconnectRtcOnly();
    }),
  );

  useSocketEvent(
    onDocsRefetch(() => {
      refetchDocs();
    }),
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    dispatch(getConversations());
    fetch();
  }, [dispatch]);

  // AI
  useSocketEvent(
    onVoiceChannelAILock((data) => {
      if (!data.lockedBy.meetId) return;
      if (data.lockedBy.meetId !== myMeetId) {
        setChannelLockedBy(data.lockedBy.meetId);
      }
    }),
  );

  useSocketEvent(
    onVoiceChannelAIUnlock(() => {
      setChannelLockedBy(null);
      setAiStatus(null);
    }),
  );

  useSocketEvent(
    onVoiceChannelAIAnswer(() => {
      setAiStatus('speaking');
    }),
  );

  useSocketEvent(
    onWorkspaceMembersRefetch(() => {
      dispatch(getConversations());
      refetchDocs();
      refetchWorkspaceInfo();
    }),
  );

  useSocketEvent(
    onBoardCreated((board) => {
      addBoardToCache(cache, {
        board: parseBoardToGql(board),
      });
    }),
  );

  useSocketEvent(
    onTableCreated((table) => {
      addBoardTableToCache(cache, { table });
    }),
  );

  useSocketEvent(
    onUserRecentsUpsert((recent) => {
      addRecent(user?.workspace._id || '', user?._id || '', recent);
    }),
  );

  useSocketEvent(
    onUserRecentsDelete((recentId) => {
      removeRecent(user?.workspace._id || '', user?._id || '', recentId);
    }),
  );
  return null;
}
